import { useQueryClient } from '@tanstack/react-query';
import { CustomCellRendererProps } from 'ag-grid-react';
import React, { useEffect, useState } from 'react';

import { useModal } from '../providers/Modal';
import { removeSingleCardFromCache } from '../services/auditService';
import { getPillarURL } from '../services/pillarUrlService';
import { selectedAudit } from '../state/audit';
import LoadingSpinner from './ui/LoadingSpinner';

interface ModalContentProps {
    cardId: number;
    auditId: number;
    url: string;
    onCancel: () => void;
}

const ModalContent: React.FC<ModalContentProps> = ({
    cardId,
    auditId,
    url,
}: ModalContentProps) => {
    const origin = new URL(url).origin;
    const cardUrl = `${origin}/audits/${auditId}/card/${cardId}`;
    return (
        <>
            <div className="h-[calc(90vh-2rem)]">
                <iframe
                    src={cardUrl}
                    title="Card"
                    style={{ width: '100%', height: '100%' }}
                />
            </div>
        </>
    );
};

function GridCellIframeContainer({ data: row }: CustomCellRendererProps) {
    const queryClient = useQueryClient();
    const cardId = row.id;
    const auditId = selectedAudit?.value?.id || 0;
    const { showModal, hideModal } = useModal();
    const [pillarUrl, setPillarUrl] = useState('');
    const archived = row.archived ? '(Descoped)' : '';

    const handleModalCancel = () => {
        hideModal();
    };

    const handleModalDestroy = async () => {
        const cardUrl = `/api/cards/${cardId}`;
        const response = await fetch(cardUrl, { method: 'DELETE' });
        if (response.ok) {
            removeSingleCardFromCache(queryClient, auditId, cardId);
            console.log('response', response);
        } else {
            throw new Error('Failed to destroy Card');
            console.error('Failed to destroy card:', response.statusText);
        }
        hideModal();
    };

    useEffect(() => {
        async function fetchData() {
            try {
                const url = await getPillarURL();
                setPillarUrl(url);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (e: any) {
                console.log('Error!!', e.message);
                throw e;
            }
        }
        fetchData();
    }, []); // Empty dependency array - only run once on mount

    const render = () => {
        if (pillarUrl == '')
            return (
                <div className="text-center w-full">
                    <LoadingSpinner />
                </div>
            );
        else {
            return (
                <>
                    <button
                        onClick={() =>
                            showModal(
                                <ModalContent
                                    onCancel={handleModalCancel}
                                    cardId={cardId}
                                    auditId={auditId}
                                    url={pillarUrl}
                                />,
                                '',
                                'w-3/4',
                            )
                        }
                        className={`px-2 py-1 mt-4 bg-gray-50 text-gray-700 border border-gray-400 rounded flex gap-3`}
                    >
                        {row.refId}
                    </button>
                    <button
                        onClick={() =>
                            showModal(
                                <div>
                                    <p>Are you sure to delete this card?</p>
                                    <div className="text-right">
                                        <button
                                            className="rounded bg-indigo-600 px-2 py-1 text-sm font-semibold text-white shadow-sm mr-2"
                                            onClick={handleModalDestroy}
                                        >
                                            Ok
                                        </button>
                                        <button
                                            className="rounded bg-indigo-600 px-2 py-1 text-sm font-semibold text-white shadow-sm"
                                            onClick={handleModalCancel}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>,
                                '',
                                'w-1/4',
                            )
                        }
                        className={`px-2 py-1 mt-4 bg-gray-50 text-gray-700 border border-gray-400 rounded flex gap-3`}
                    >
                        DELETE
                    </button>
                    <p className="p-1">{archived}</p>
                </>
            );
        }
    };

    return (
        <div className="flex flex-col">
            <div className="flex flex-col flex-grow align-bottom items-start text-xs font-medium">
                {render()}
            </div>
        </div>
    );
}

export default GridCellIframeContainer;
